import { InputAdornment, MenuItem, TextField } from '@mui/material';
import { Controller, FieldPathValue, Path } from 'react-hook-form';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import { ReactNode } from 'react';
import { useFormTracking, useFormOptionTracking } from '../hooks';

export type SelectOption<VALUE> = {
  label: ReactNode;
  value: VALUE;
  notSelectable?: boolean;
  /** Optional node rendered after the label (e.g. an indicator icon). */
  endAdornment?: ReactNode;
};

type SelectProps<VALUE> = {
  options: SelectOption<VALUE>[];
  fullWidth?: boolean;
};

export type ControlledSelectProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = Omit<
  BaseControlledInputProps<FORM_TYPE, FIELD_NAME>,
  'onBlurCallback' | 'onFocusCallback'
> &
  SelectProps<FieldPathValue<FORM_TYPE, FIELD_NAME>> & {};

export const ControlledSelect = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  ariaLabel,
  ariaRole,
  defaultValue = undefined,
  disabled,
  hidden,
  iconAdornment,
  control,
  required,
  options,
  fullWidth = false,
  trackingName,
  sensitive = true,
}: ControlledSelectProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? `${label} is required` : false }}
          defaultValue={defaultValue}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            // Convert empty string back to null when onChange is called
            const handleChange = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              const newValue = event.target.value;
              onChange(newValue === '' ? null : newValue);
            };

            return (
              !hidden && (
                <TextField
                  {...trackingProps}
                  fullWidth={fullWidth}
                  onChange={handleChange}
                  disabled={disabled}
                  defaultValue={defaultValue}
                  label={label}
                  value={value || defaultValue || ''}
                  aria-label={ariaLabel}
                  role={ariaRole}
                  select
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {iconAdornment}
                      </InputAdornment>
                    ),
                  }}
                >
                  {options.map(option => {
                    if (
                      option.value !== null &&
                      typeof option.value !== 'string' &&
                      typeof option.value !== 'number'
                    ) {
                      throw new Error(
                        `Option value must be null, string, or number. Field ${name} has an invalid option value ${option.value}`
                      );
                    }
                    // Convert null to empty string for MUI Select compatibility
                    const menuItemValue = (option.value ?? '') as
                      | string
                      | number;
                    return (
                      <MenuItem
                        key={
                          option.value !== null
                            ? String(option.value)
                            : 'null-option'
                        }
                        value={menuItemValue}
                        disabled={option.notSelectable}
                        {...generateOptionTracking(
                          option.value,
                          String(option.label)
                        )}
                      >
                        {option.label}
                        {option.endAdornment}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )
            );
          }}
        />
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};
